import { SENDA } from '@somnhold/content';
import { ANGLE_STEPS, iatan2, idiv, imax, imin, mulBp, withinRange } from '../math/fixed.ts';
import type { ArchRules, ArchState, BattleState, Enemy, GuardRules, Hero, SendaNpc } from '../types.ts';
import { damageEnemy, ev, spawnEnemy } from './combat.ts';
import { stepToward } from './enemies.ts';
import { orderMove } from './heroes.ts';
import { guardTick } from './keep.ts';
import { sendaCue } from './senda.ts';
import { ARCH_FOOT, ARCH_HEAD, STAIR_HALF, type ArchLayout } from './arch-layout.ts';

/**
 * The bridge's altar (docs/sendas/paladin.md, chapter 3; §4, «El aljibe del puente»). A stone bridge of one arch in the
 * rain; the fourth altar in its middle over a cistern carved in the arch; bands of nightmares up the stairs of both
 * ends, seen in the clouds below a moment before. Ser Marit keeps the lower end, Ser Varo and his two knights the upper.
 *
 * - The aura: a keeper within the paladin's aura of the standing hero is covered: of its blows only `coverBp` count
 *   (`archCover`, from battle/senda.ts). Beside the altar it reaches both ends; anywhere else, one at most.
 * - The two ends (act 1): the bands by turns, now and then by both; won with the last band down.
 * - The gutters (act 2): the heavy rain falls on one half of the bridge at a time. The cistern rises a mark a `step`
 *   with the rain side's gate open and the other shut; with the dry side's open it falls one; a nightmare on the rain
 *   side's gutter stops it. A gargoyle (its chain by the cistern) spends four marks and sweeps its stairs: whatever is
 *   on them goes four hexes down and lies there. Some of each band go to stand on their end's gutter. Won full.
 * - The night (act 3, the test): the altar burns while the cistern holds half; below it a while, it goes out. Fallen
 *   sentinels come up with an escort, their shields letting little through from the front; swept, they stand up side-on
 *   and the shield doesn't turn for a while. Two ash mammoths climb the stairs (the second by Varo's end), lower their
 *   head at the top and charge the altar; a gargoyle opened while the head is down throws one to the foot with a third
 *   of its life gone. Won with both down.
 *
 * Cost: the keepers and the levers are a handful; the swept, the sentinels side-on and the blockers are short sorted
 * lists; the gutters look at the nightmares once a tick (docs/RENDIMIENTO.md, «Reglas para lo nuevo»).
 */

/** The bridge's rules from its layout (the fight's builder, campaign/sendas.ts; and the renderer's scenes). */
export const archRules = (L: ArchLayout, kind: ArchRules['kind'], marit: GuardRules, sentinelHp: number, mammothHp: number): ArchRules => {
  const A = SENDA.arch;
  const at: number[] = [];
  const side: number[] = [];
  const act = kind === 'ends' ? A.ends : kind === 'gutters' ? A.gutters : A.vigil;
  const count = kind === 'ends' ? A.ends.count : 40;
  for (let i = 0; i < count && i < L.sides.length; i++) {
    at.push(act.first + i * act.every);
    side.push(L.sides[i] as number);
  }
  const V = A.vigil;
  return {
    kind,
    head: ARCH_HEAD,
    foot: ARCH_FOOT,
    keeps: Object.entries(L.keeps)
      .map(([id, p]) => ({ id, x: p.x, y: p.y }))
      .sort((a, b) => (a.id < b.id ? -1 : a.id > b.id ? 1 : 0)),
    marit,
    aura: A.aura,
    coverBp: A.coverBp,
    rain: { first: L.rain, every: A.rain.every, warn: A.rain.warn },
    bands: {
      at,
      side,
      kinds: act.kinds.slice(),
      n: act.n,
      both: act.both,
      warn: A.bands.warn,
      blockers: kind === 'gutters' ? A.gutters.blockers : 0,
      escort: kind === 'vigil' ? V.escort : 0,
      sentinelHp,
    },
    levers: L.levers.map((p) => ({ x: p.x, y: p.y })),
    turn: A.lever.turn,
    reach: A.lever.reach,
    gutters: L.gutters.map((g) => ({ x0: g.x0, x1: g.x1, y0: g.y0, y1: g.y1, stand: { x: g.stand.x, y: g.stand.y } })),
    ...(kind !== 'ends' ? { cistern: { cap: A.gutters.cap, start: kind === 'vigil' ? V.start : A.gutters.start, step: A.gutters.step, lit: V.lit, dark: V.dark } } : {}),
    sweep: { drop: A.sweep.drop, push: A.sweep.push, pace: A.sweep.pace, down: A.sweep.down },
    ...(kind === 'vigil'
      ? {
          sentinel: { every: V.sentinel.every, arc: V.sentinel.arc, frontBp: V.sentinel.frontBp, side: V.sentinel.side },
          mammoth: { hp: mammothHp, at: V.mammoth.at.slice(), climb: V.mammoth.climb, lower: V.mammoth.lower, charge: V.mammoth.charge, sweepBp: V.mammoth.sweepBp },
        }
      : {}),
  };
};

/** The bridge as a fight starts (the renderer's scenes build one too). */
export const archFresh = (r: ArchRules, npcs = 0): ArchState => ({
  rain: r.rain.first,
  rainAt: r.rain.every,
  rainWarned: false,
  band: 0,
  warned: false,
  level: r.cistern?.start ?? 0,
  acc: 0,
  open: [0, 0],
  blocked: [0, 0],
  job: -1,
  held: 0,
  pushId: [],
  pushLeft: [],
  heroPush: 0,
  heroSide: 0,
  sideId: [],
  sideUntil: [],
  blockId: [],
  blockAt: [],
  mammoth: [0, 0],
  mode: [0, 0],
  modeT: [0, 0],
  felled: 0,
  sweeps: 0,
  thrown: 0,
  dark: -1,
  out: false,
  swing: new Array<number>(npcs).fill(0),
  cov: new Array<number>(npcs).fill(0),
  covered: new Array<number>(npcs).fill(0),
  done: false,
});

/** Index of a sorted list's value (−1: not in it). */
const find = (list: readonly number[], v: number): number => {
  let lo = 0;
  let hi = list.length - 1;
  while (lo <= hi) {
    const m = (lo + hi) >> 1;
    const x = list[m] as number;
    if (x === v) return m;
    if (x < v) lo = m + 1;
    else hi = m - 1;
  }
  return -1;
};

/** Where a value goes in a sorted list. */
const slot = (list: readonly number[], v: number): number => {
  let i = 0;
  while (i < list.length && (list[i] as number) < v) i++;
  return i;
};

const npcOf = (s: BattleState, id: string): SendaNpc | undefined => s.senda?.npcs.find((n) => n.id === id);

/** Which way is down the stairs of a side: +y the lower (0), −y the upper (1). */
const down = (side: number): number => (side === 0 ? 1 : -1);

/** Whether a point is on a side's stairs (from a little above their head, where the gargoyle's water falls). */
export const archOnStairs = (r: ArchRules, side: number, y: number): boolean => (side === 0 ? y >= r.head - 300 : y <= -r.head + 300);

/** Whether the standing hero's aura covers a point. */
export const archCovers = (s: BattleState, x: number, y: number): boolean => {
  const r = s.rules?.senda?.arch;
  if (!r) return false;
  for (const h of s.heroes) if (h.downTicks === 0 && withinRange(h.x, h.y, x, y, r.aura)) return true;
  return false;
};

/** A keeper's blows this second under the aura (battle/senda.ts `struck`): `coverBp` of them, the rest kept for later. */
export const archCover = (s: BattleState, n: SendaNpc, got: number): number => {
  const r = s.rules?.senda?.arch;
  const w = s.senda?.arch;
  if (!r || !w || !r.keeps.some((k) => k.id === n.id)) return got;
  const i = s.senda?.npcs.indexOf(n) ?? -1;
  if (i < 0 || !archCovers(s, n.x, n.y)) return got;
  const acc = (w.cov[i] ?? 0) + got * r.coverBp;
  const take = idiv(acc, 10000);
  w.cov[i] = acc - take * 10000;
  return take;
};

/** The hero sent to a lever (the two gates, the two gargoyles): it goes and works it. */
export const archOrder = (s: BattleState, h: Hero, at: number): void => {
  const r = s.rules?.senda?.arch;
  const w = s.senda?.arch;
  if (!r || !w || !r.cistern || at < 0 || at >= r.levers.length) return;
  w.job = at;
  w.held = 0;
  const p = r.levers[at] as { x: number; y: number };
  orderMove(s, h, p.x, p.y);
  h.target = 0;
  h.forced = false;
};

/** A blow on a sentinel of the night (combat's damage): from in front of its shield, `frontBp` of it; side-on, whole. */
export const archGuard = (s: BattleState, e: Enemy, dmg: number, fromX: number, fromY: number): number => {
  const r = s.rules?.senda?.arch;
  const w = s.senda?.arch;
  if (!r?.sentinel || !w || e.kind !== 'sentinel') return dmg;
  const i = find(w.sideId, e.id);
  if (i >= 0 && (w.sideUntil[i] as number) > s.tick) return dmg;
  const d = ((((iatan2(fromY - e.y, fromX - e.x) - e.facing) % ANGLE_STEPS) + ANGLE_STEPS + ANGLE_STEPS / 2) % ANGLE_STEPS) - ANGLE_STEPS / 2;
  if (d > r.sentinel.arc || d < -r.sentinel.arc) return dmg;
  if (s.tick % 6 === 0) ev(s, { t: 'boom', x: e.x, y: e.y, r: 400, kind: 'blocked' });
  return mulBp(dmg, r.sentinel.frontBp);
};

/** Whether a sentinel stands side-on now (for the renderer and the bot). */
export const archSideOn = (s: BattleState, e: Enemy): boolean => {
  const w = s.senda?.arch;
  if (!w || e.kind !== 'sentinel') return false;
  const i = find(w.sideId, e.id);
  return i >= 0 && (w.sideUntil[i] as number) > s.tick;
};

/* ---------- the bands ---------- */

const spawnBand = (s: BattleState, r: ArchRules, w: ArchState, i: number): void => {
  const B = r.bands;
  const side = B.side[i] as number;
  const sides = side === 2 ? [0, 1] : [side];
  for (const sd of sides) {
    const n = side === 2 ? B.both : B.n;
    const dir = down(sd);
    let blockers = B.blockers;
    // The night's: a sentinel at the head of its escort.
    const list: { kind: string; elite: boolean }[] = [];
    if (r.sentinel && i % r.sentinel.every === 0) {
      list.push({ kind: 'sentinel', elite: true });
      for (let k = 0; k < (side === 2 ? imax(1, B.escort - 1) : B.escort); k++) list.push({ kind: B.kinds[k % B.kinds.length] as string, elite: false });
    } else for (let k = 0; k < n; k++) list.push({ kind: B.kinds[(i + k) % B.kinds.length] as string, elite: false });
    list.forEach((f, k) => {
      const x = (k % 2 === 0 ? -1 : 1) * (250 + (k >> 1) * 150);
      const y = dir * (r.foot - k * 380);
      const e = spawnEnemy(s, f.kind as Enemy['kind'], 1, f.elite, 0, x, y, f.kind === 'sentinel' ? B.sentinelHp : 0);
      if (f.kind !== 'sentinel' && s.rules?.enemyHpBp) e.hp = e.maxHp = imax(1, mulBp(e.maxHp, s.rules.enemyHpBp));
      e.facing = iatan2(-dir, 0);
      e.anim = 'walk';
      if (blockers > 0) {
        blockers--;
        const at = slot(w.blockId, e.id);
        w.blockId.splice(at, 0, e.id);
        w.blockAt.splice(at, 0, sd);
      }
    });
  }
};

/* ---------- the gargoyles ---------- */

/** A gargoyle's water down its stairs: everything on them pushed down; a mammoth with its head down, to the foot. */
const sweep = (s: BattleState, r: ArchRules, w: ArchState, side: number, cue: (c: string) => void): void => {
  w.sweeps++;
  for (const e of s.enemies) {
    if (e.hp <= 0 || !archOnStairs(r, side, e.y)) continue;
    let left = r.sweep.push;
    const k = w.mammoth.indexOf(e.id);
    if (k >= 0 && w.mode[k] === 1 && r.mammoth) {
      // Head down for its charge: the water takes its feet, it goes all the way down, a third of it gone.
      left = r.foot - Math.abs(e.y);
      w.mode[k] = 0;
      w.thrown++;
      damageEnemy(s, e, mulBp(e.maxHp, r.mammoth.sweepBp), { pid: '', ignoresArmor: true });
      cue('thrown');
    } else if (k >= 0 && w.mode[k] === 2) continue;
    left = imax(0, imin(left, r.foot - Math.abs(e.y)));
    const i = find(w.pushId, e.id);
    if (i >= 0) w.pushLeft[i] = imax(w.pushLeft[i] as number, left);
    else {
      const at = slot(w.pushId, e.id);
      w.pushId.splice(at, 0, e.id);
      w.pushLeft.splice(at, 0, left);
    }
    e.anim = 'hit';
    e.animT = 0;
  }
  for (const h of s.heroes) {
    if (h.downTicks > 0 || !archOnStairs(r, side, h.y)) continue;
    w.heroPush = imax(0, imin(r.sweep.push, r.foot - Math.abs(h.y)));
    w.heroSide = side;
  }
};

/* ---------- each tick ---------- */

const lever = (s: BattleState, r: ArchRules, w: ArchState, cue: (c: string) => void): void => {
  const h = s.heroes.find((x) => x.downTicks === 0);
  if (!h || w.job < 0 || !r.cistern) return;
  const p = r.levers[w.job] as { x: number; y: number };
  if (h.moving || !withinRange(h.x, h.y, p.x, p.y, r.reach)) {
    w.held = 0;
    if (!h.moving && !withinRange(h.x, h.y, p.x, p.y, r.reach)) w.job = -1;
    return;
  }
  if (++w.held < r.turn) return;
  const j = w.job;
  w.job = -1;
  w.held = 0;
  if (j < 2) {
    w.open[j] = w.open[j] ? 0 : 1;
    ev(s, { t: 'senda', kind: 'gutter', npc: '', x: p.x, y: p.y, n: w.open[j] as number });
    return;
  }
  const side = j - 2;
  if (w.level < r.sweep.drop) {
    ev(s, { t: 'senda', kind: 'stuck', npc: '', x: p.x, y: p.y, n: j });
    return;
  }
  w.level -= r.sweep.drop;
  w.acc = 0;
  ev(s, { t: 'senda', kind: 'gargoyle', npc: '', x: 0, y: (side === 0 ? 1 : -1) * r.head, n: side });
  ev(s, { t: 'senda', kind: 'mark', npc: '', x: 0, y: 0, n: w.level });
  cue('gargoyle');
  sweep(s, r, w, side, cue);
};

/** The rain into the cistern by the gutters (and out by the dry side's). */
const fill = (s: BattleState, r: ArchRules, w: ArchState, cue: (c: string) => void): void => {
  const C = r.cistern;
  if (!C) return;
  w.blocked[0] = 0;
  w.blocked[1] = 0;
  for (const e of s.enemies) {
    if (e.hp <= 0 || e.kind === 'mammoth') continue;
    for (let k = 0; k < 2; k++) {
      const g = r.gutters[k] as ArchRules['gutters'][number];
      if (e.x >= g.x0 && e.x <= g.x1 && e.y >= g.y0 && e.y <= g.y1) w.blocked[k] = 1;
    }
  }
  const wet = w.rain;
  const dry = 1 - wet;
  const dir = w.open[dry] ? -1 : w.open[wet] && !w.blocked[wet] ? 1 : 0;
  if (dir > 0 && w.level < C.cap) {
    w.acc = imax(w.acc, 0) + 1;
    if (w.acc >= C.step) {
      w.level++;
      w.acc = 0;
      ev(s, { t: 'senda', kind: 'mark', npc: '', x: 0, y: 0, n: w.level });
      if (w.level >= C.cap) cue('full');
    }
  } else if (dir < 0 && w.level > 0) {
    w.acc = imin(w.acc, 0) - 1;
    if (w.acc <= -C.step) {
      w.level--;
      w.acc = 0;
      ev(s, { t: 'senda', kind: 'mark', npc: '', x: 0, y: 0, n: w.level });
    }
  } else w.acc = 0; // (Still water: a mark counts a whole `step` of running, not what was left from before.)
  // The night: the altar burns while the cistern holds half; below it, a while to save it.
  if (r.kind === 'vigil' && !w.out) {
    if (w.level >= C.lit) w.dark = -1;
    else if (w.dark < 0) {
      w.dark = s.tick;
      ev(s, { t: 'senda', kind: 'out', npc: '', x: 0, y: 0, n: 0 });
    } else if (s.tick - w.dark >= C.dark) {
      w.out = true;
      const a = npcOf(s, 'altar4');
      if (a) a.hp = 0;
      ev(s, { t: 'senda', kind: 'out', npc: 'altar4', x: 0, y: 0, n: 1 });
    }
  }
};

const mammoths = (s: BattleState, r: ArchRules, w: ArchState, cue: (c: string) => void): void => {
  const M = r.mammoth;
  if (!M) return;
  const T = s.waveT;
  for (let k = 0; k < 2; k++) {
    const id = w.mammoth[k] as number;
    if (id === 0 && T >= (M.at[k] as number)) {
      const dir = down(k);
      const e = spawnEnemy(s, 'mammoth', 1, false, 0, 0, dir * r.foot, M.hp);
      e.facing = iatan2(-dir, 0);
      e.anim = 'walk';
      w.mammoth[k] = e.id;
      w.mode[k] = 0;
      cue(`mammoth${k + 1}`);
    } else if (id > 0 && !s.enemies.some((e) => e.id === id && e.hp > 0)) {
      w.mammoth[k] = -1;
      w.felled++;
      cue(`felled${w.felled}`);
    }
  }
};

/** The swept going down, a mammoth's climb and charge, the blockers to their gutter (from the enemies' tick). */
export const archHold = (s: BattleState, e: Enemy, pace: (e: Enemy) => number): boolean => {
  const r = s.rules?.senda?.arch;
  const w = s.senda?.arch;
  if (!r || !w) return false;
  const pi = w.pushId.length ? find(w.pushId, e.id) : -1;
  if (pi >= 0) {
    const left = w.pushLeft[pi] as number;
    const step = imin(left, r.sweep.pace);
    e.y += step * (e.y > 0 ? 1 : -1);
    if (e.x > STAIR_HALF - 250) e.x = STAIR_HALF - 250;
    if (e.x < -STAIR_HALF + 250) e.x = -STAIR_HALF + 250;
    e.anim = 'hit';
    if (left - step <= 0) {
      w.pushId.splice(pi, 1);
      w.pushLeft.splice(pi, 1);
      e.stun = imax(e.stun, r.sweep.down);
      if (r.sentinel && e.kind === 'sentinel') {
        // It gets up side-on, and the shield doesn't turn for a while.
        const until = s.tick + r.sweep.down + r.sentinel.side;
        const i = find(w.sideId, e.id);
        if (i >= 0) w.sideUntil[i] = until;
        else {
          const at = slot(w.sideId, e.id);
          w.sideId.splice(at, 0, e.id);
          w.sideUntil.splice(at, 0, until);
        }
        e.facing = (e.facing + (ANGLE_STEPS >> 2)) % ANGLE_STEPS;
        ev(s, { t: 'senda', kind: 'side', npc: '', x: e.x, y: e.y });
        sendaCue(s, 'side');
      }
    } else w.pushLeft[pi] = left - step;
    return true;
  }
  const k = r.mammoth ? w.mammoth.indexOf(e.id) : -1;
  if (k >= 0 && r.mammoth) return mammothMove(s, r, w, e, k);
  if (w.blockId.length) {
    const bi = find(w.blockId, e.id);
    if (bi >= 0) {
      const h = s.heroes.find((x) => x.downTicks === 0 && withinRange(x.x, x.y, e.x, e.y, 1200));
      if (h || e.stun > 0) return false;
      const g = r.gutters[w.blockAt[bi] as number] as ArchRules['gutters'][number];
      if (stepToward(e, g.stand.x, g.stand.y, pace(e))) e.anim = 'idle';
      else e.anim = 'walk';
      return true;
    }
  }
  return false;
};

const mammothMove = (s: BattleState, r: ArchRules, w: ArchState, e: Enemy, k: number): boolean => {
  const M = r.mammoth as NonNullable<ArchRules['mammoth']>;
  if (e.stun > 0) {
    e.stun--;
    e.anim = 'stun';
    return true;
  }
  const dir = down(k);
  const mode = w.mode[k] as number;
  if (mode === 0) {
    // Straight up its stairs, whatever stands there (it never turns for a hero).
    const px = idiv(r.foot - r.head + M.climb - 1, M.climb);
    e.anim = 'walk';
    if (stepToward(e, 0, dir * r.head, px)) {
      w.mode[k] = 1;
      w.modeT[k] = s.tick + M.lower;
      e.anim = 'cast';
      ev(s, { t: 'senda', kind: 'lower', npc: '', x: e.x, y: e.y, n: k });
      sendaCue(s, 'lower');
    }
    return true;
  }
  if (mode === 1) {
    e.anim = 'cast';
    e.facing = iatan2(-dir, 0);
    if (s.tick >= (w.modeT[k] as number)) {
      w.mode[k] = 2;
      ev(s, { t: 'senda', kind: 'charge', npc: '', x: e.x, y: e.y, n: k });
    }
    return true;
  }
  // The charge: at the altar, and over it.
  e.anim = 'walk';
  const a = npcOf(s, 'altar4');
  if (stepToward(e, 0, dir * 700, M.charge) || withinRange(e.x, e.y, 0, 0, 900)) {
    if (a && a.hp > 0) {
      a.hp = 0;
      ev(s, { t: 'senda', kind: 'crush', npc: 'altar4', x: 0, y: 0, n: k });
    }
    e.anim = 'attack';
  }
  return true;
};

export const archTick = (s: BattleState, cue: (c: string) => void): void => {
  const r = s.rules?.senda?.arch;
  const st = s.senda;
  if (!r || !st) return;
  if (!st.arch) st.arch = archFresh(r, st.npcs.length);
  const w = st.arch;
  if (!s.started) return;
  const T = s.waveT;
  // The heavy rain moving from one half to the other.
  if (!w.rainWarned && T >= w.rainAt - r.rain.warn) {
    w.rainWarned = true;
    ev(s, { t: 'senda', kind: 'rainWarn', npc: '', x: 0, y: 0, n: 1 - w.rain });
  }
  if (T >= w.rainAt) {
    w.rain = 1 - w.rain;
    w.rainAt = T + r.rain.every;
    w.rainWarned = false;
    ev(s, { t: 'senda', kind: 'rain', npc: '', x: 0, y: 0, n: w.rain });
  }
  // The bands, seen in the clouds below a moment before.
  const B = r.bands;
  if (w.band < B.at.length) {
    const at = B.at[w.band] as number;
    if (!w.warned && T >= at - B.warn) {
      w.warned = true;
      const side = B.side[w.band] as number;
      ev(s, { t: 'senda', kind: 'bandWarn', npc: '', x: 0, y: side === 1 ? -r.foot : side === 0 ? r.foot : 0, n: side });
      if (w.band === 0) cue('first');
      if (side === 2) cue('both');
    }
    if (T >= at) {
      spawnBand(s, r, w, w.band);
      w.band++;
      w.warned = false;
    }
  } else w.done = true;
  mammoths(s, r, w, cue);
  lever(s, r, w, cue);
  fill(s, r, w, cue);
  // The hero swept down its stairs.
  if (w.heroPush > 0) {
    const h = s.heroes.find((x) => x.downTicks === 0);
    if (!h) w.heroPush = 0;
    else {
      const step = imin(w.heroPush, r.sweep.pace);
      h.y += step * down(w.heroSide);
      h.destX = h.ax = h.x;
      h.destY = h.ay = h.y;
      h.path = [];
      h.moving = false;
      w.heroPush -= step;
      if (w.heroPush <= 0) h.stun = imax(h.stun, r.sweep.down);
    }
  }
  // The keepers fight where they keep; who the aura covers now.
  const swing = w.swing;
  for (let i = 0; i < st.npcs.length; i++) {
    const n = st.npcs[i] as SendaNpc;
    const keep = r.keeps.find((k) => k.id === n.id);
    w.covered[i] = keep && n.hp > 0 && archCovers(s, n.x, n.y) ? 1 : 0;
    if (!keep || n.hp <= 0) continue;
    swing[i] = guardTick(s, n, keep, r.marit, swing[i] ?? 0, (e) => w.mammoth.includes(e.id) && w.mode[w.mammoth.indexOf(e.id)] === 2);
  }
  // The dead off the short lists now and then.
  if (s.tick % 24 === 0) {
    for (let i = w.blockId.length - 1; i >= 0; i--) {
      const id = w.blockId[i] as number;
      if (s.enemies.some((e) => e.id === id && e.hp > 0)) continue;
      w.blockId.splice(i, 1);
      w.blockAt.splice(i, 1);
    }
    for (let i = w.sideId.length - 1; i >= 0; i--) {
      if ((w.sideUntil[i] as number) > s.tick) continue;
      w.sideId.splice(i, 1);
      w.sideUntil.splice(i, 1);
    }
    for (let i = w.pushId.length - 1; i >= 0; i--) {
      const id = w.pushId[i] as number;
      if (s.enemies.some((e) => e.id === id && e.hp > 0)) continue;
      w.pushId.splice(i, 1);
      w.pushLeft.splice(i, 1);
    }
  }
};

/** The two ends: the bands down; the gutters: the cistern's marks; the night: the mammoths down. */
export const archGoal = (s: BattleState): { done: number; need: number } => {
  const r = s.rules?.senda?.arch;
  const w = s.senda?.arch;
  if (!r || !w) return { done: 0, need: 1 };
  if (r.kind === 'ends') {
    const alive = s.enemies.some((e) => e.hp > 0);
    return { done: imax(0, w.band - (alive ? 1 : 0)), need: r.bands.at.length };
  }
  if (r.kind === 'gutters') return { done: w.level, need: r.cistern?.cap ?? 1 };
  return { done: w.felled, need: 2 };
};

export const archResult = (s: BattleState): 'won' | 'lost' | null => {
  const r = s.rules?.senda?.arch;
  const w = s.senda?.arch;
  if (!r || !w) return null;
  if (r.kind === 'ends') return w.done && !s.enemies.some((e) => e.hp > 0) ? 'won' : null;
  if (r.kind === 'gutters') return w.level >= (r.cistern?.cap ?? 1) ? 'won' : null;
  if (w.out) return 'lost';
  return w.felled >= 2 ? 'won' : null;
};
